// ============================================================================
// PlanningSignatureGate.tsx — Rappel de signature à la connexion.
// ----------------------------------------------------------------------------
// À chaque session, si le salarié n'a PAS signé le planning du mois à signer
// (mois écoulé / en cours de clôture, règle 28→5) ALORS qu'il avait des créneaux
// publiés ce mois-là, on ouvre le dialogue de signature. « Plus tard » repousse
// à la prochaine connexion (dismiss stocké en sessionStorage par mois).
// Monté une fois dans AppShell.
// ============================================================================
import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import { Capability } from '@magic/shared';
import { supabase } from '@/lib/supabase';
import { SignScheduleDialog } from './SignScheduleDialog';
import { monthKey, monthBounds, targetSignMonth } from './schedule-signature';

export function PlanningSignatureGate() {
  const { me, hasCapability } = useAuth();
  const [askMonth, setAskMonth] = useState<Date | null>(null);

  useEffect(() => {
    if (!me || !hasCapability(Capability.PLANNING_SIGN)) return;
    const uid = me.profile.id;
    const month = targetSignMonth();
    const dismissKey = `planning-sign-dismissed:${uid}:${monthKey(month)}`;
    if (sessionStorage.getItem(dismissKey)) return;

    let cancelled = false;
    void (async () => {
      // Déjà signé ce mois ?
      const { data: sig } = await supabase.schema('planning').from('schedule_signatures')
        .select('id').eq('user_id', uid).eq('period_month', monthKey(month)).maybeSingle();
      if (cancelled || sig) return;
      // A-t-il des créneaux publiés ce mois-là ? (sinon rien à signer)
      const { from, to } = monthBounds(month);
      const { data: sh } = await supabase.schema('planning').from('shifts')
        .select('id').eq('user_id', uid).eq('status', 'published').gte('date', from).lte('date', to).limit(1);
      if (cancelled || !sh || sh.length === 0) return;
      setAskMonth(month);
    })();
    return () => { cancelled = true; };
  }, [me, hasCapability]);

  if (!askMonth || !me) return null;

  const dismissKey = `planning-sign-dismissed:${me.profile.id}:${monthKey(askMonth)}`;
  const name = `${me.profile.first_name ?? ''} ${me.profile.last_name ?? ''}`.trim() || (me.profile.first_name ?? 'Salarié');

  return (
    <SignScheduleDialog
      month={askMonth}
      userId={me.profile.id}
      signerName={name}
      onClose={() => { sessionStorage.setItem(dismissKey, '1'); setAskMonth(null); }}
      onSigned={() => setAskMonth(null)}
    />
  );
}
